import { useState, useRef } from "react";
import { Mail, Shield, Lock, Eye, EyeOff, User } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import whitelogo from "../assets/whiteintel-white-logo-new.png";

const REGISTER_URL = "https://whiteintel.io/api/regular/app/signup_handler.php";

export default function Register() {
  const [name, setName] = useState("");
  const [surName, setSurName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);

  const handleRegister = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess(false);

    if (password !== confirmPw) {
      setErr("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(REGISTER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          surname: surName.trim(),
          company: company.trim(),
          email: email.trim(),
          password,
          reTypePassword: confirmPw,
          "cf-turnstile-response": turnstileToken,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Registration failed");

      setSuccess(true);
    } catch (e) {
      setErr(e.message || "Registration failed");
    } finally {
      setLoading(false);
      if (turnstileRef.current) turnstileRef.current.reset();
      setTurnstileToken("");
    }
  };

  return (
    <div className="min-h-screen bg-[#000414] text-white grid grid-cols-1 md:grid-cols-2">
      {/* Left brand panel (desktop only) */}
      <aside className="relative hidden md:flex flex-col border-r border-neutral-800 p-10
                        bg-neutral-900/40 shadow-[0_0_30px_rgba(56,189,248,0.06)] backdrop-blur overflow-hidden">
        {/* Background: dots */}
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url("data:image/svg+xml;utf8,
              <svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'>
                <circle cx='1.25' cy='1.25' r='1.25' fill='%23a3b3c5' fill-opacity='0.35'/>
              </svg>")`,
            backgroundSize: "20px 20px",
          }}
        />
        {/* Background: soft glow */}
        <div
          className="pointer-events-none absolute -right-40 -top-32 h-80 w-80 rounded-full blur-3xl opacity-30"
          style={{ background: "linear-gradient(180deg, rgba(56,189,248,0.25), rgba(99,102,241,0.15))" }}
        />
        {/* Background: vignette */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(900px 450px at 10% 0%, rgba(56,189,248,0.10) 0%, rgba(0,0,0,0) 60%)",
          }}
        />

        {/* Content */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-start pt-[24vh] text-center">
          <img src={whitelogo} alt="WhiteIntel" className="h-48 w-48 md:h-48 md:w-48 rounded-2xl" />
          <div
            className="mt-4 text-3xl font-bold leading-tight tracking-wide"
            style={{
              fontFamily:
                '"Outfit", ui-sans-serif, system-ui, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            }}
          >
            Whiteintel Dark Web Intelligence Services
          </div>
      <p className="mt-3 max-w-sm text-sm text-neutral-400 leading-relaxed">
        Real-time leak intelligence and stealer monitoring for enterprises, MSSPs and security teams. Start with a free account. No credit card required.
      </p>
        </div>

        {/* Footer links + version */}
        <div className="relative z-10 absolute inset-x-0 bottom-6">
          <div className="mx-auto flex flex-col items-center justify-center gap-5 text-xs text-neutral-500">
            <div className="flex items-center justify-center gap-4">
              <a href="https://whiteintel.io" className="underline" target="_blank" rel="noreferrer">
                Home
              </a>
              <a
                href="https://knowledge.whiteintel.io/"
                className="underline"
                target="_blank"
                rel="noreferrer"
              >
                Knowledge Center
              </a>
              <a href="https://whiteintel.io/terms" className="underline" target="_blank" rel="noreferrer">
                Terms
              </a>
              <a
                href="https://whiteintel.io/privacy"
                className="underline"
                target="_blank"
                rel="noreferrer"
              >
                Privacy
              </a>
            </div>
            <div className="text-[12px] text-neutral-600">v4.0.3 © 2025 Whiteintel</div>
          </div>
        </div>
      </aside>

      {/* Right: form panel */}
      <main className="flex items-center justify-center px-6 md:px-10 py-10">
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-neutral-800 bg-neutral-900/40 shadow-[0_0_30px_rgba(56,189,248,0.06)] backdrop-blur">
            <div className="px-8 pt-8 pb-6">
              {/* Small brand row for mobile */}
            <div className="md:hidden flex items-center gap-2 text-large text-neutral-400">
                <img src={whitelogo} alt="WhiteIntel" className="h-8 w-8" />
                <span>Whiteintel</span>
              </div>
              <h1 className="mt-1 text-2xl font-semibold">Create your account</h1>

              {err && (
                <div className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                  {err}
                </div>
              )}
              {success && (
                <div className="mt-4 rounded-lg border border-green-500/40 bg-green-500/10 px-3 py-2 text-sm text-green-300">
                  Your account has been created. Please check your email (including your Spam/Junk folder) to complete
                  the registration.{" "}
                  <a href="/login" className="underline text-sky-300">
                    Sign in
                  </a>
                </div>
              )}

              {!success && (
                <form onSubmit={handleRegister} className="mt-6 space-y-4">
                  <div>
                    <label className="mb-1 block text-sm text-neutral-300">Name</label>
                    <div className="flex items-center gap-2 rounded-lg border border-neutral-800 bg-[#000414] px-3 py-2.5 focus-within:border-sky-500/50">
                      <User className="h-4 w-4 text-neutral-400" />
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                        className="w-full bg-transparent outline-none placeholder:text-neutral-500 text-[15px]"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm text-neutral-300">Surname</label>
                    <div className="flex items-center gap-2 rounded-lg border border-neutral-800 bg-[#000414] px-3 py-2.5 focus-within:border-sky-500/50">
                      <User className="h-4 w-4 text-neutral-400" />
                      <input
                        type="text"
                        value={surName}
                        onChange={(e) => setSurName(e.target.value)}
                        placeholder="Your surname"
                        className="w-full bg-transparent outline-none placeholder:text-neutral-500 text-[15px]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm text-neutral-300">Company</label>
                    <div className="flex items-center gap-2 rounded-lg border border-neutral-800 bg-[#000414] px-3 py-2.5 focus-within:border-sky-500/50">
                      <User className="h-4 w-4 text-neutral-400" />
                      <input
                        type="text"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        placeholder="Your company"
                        className="w-full bg-transparent outline-none placeholder:text-neutral-500 text-[15px]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm text-neutral-300">Email</label>
                    <div className="flex items-center gap-2 rounded-lg border border-neutral-800 bg-[#000414] px-3 py-2.5 focus-within:border-sky-500/50">
                      <Mail className="h-4 w-4 text-neutral-400" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        className="w-full bg-transparent outline-none placeholder:text-neutral-500 text-[15px]"
                        autoComplete="email"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm text-neutral-300">Password</label>
                    <div className="flex items-center gap-2 rounded-lg border border-neutral-800 bg-[#000414] px-3 py-2.5 focus-within:border-sky-500/50">
                      <Lock className="h-4 w-4 text-neutral-400" />
                      <input
                        type={showPw ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full bg-transparent outline-none placeholder:text-neutral-500 text-[15px]"
                        autoComplete="new-password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPw((s) => !s)}
                        className="p-1 text-neutral-400 hover:text-neutral-200"
                        aria-label={showPw ? "Hide password" : "Show password"}
                      >
                        {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm text-neutral-300">Confirm password</label>
                    <div className="flex items-center gap-2 rounded-lg border border-neutral-800 bg-[#000414] px-3 py-2.5 focus-within:border-sky-500/50">
                      <Lock className="h-4 w-4 text-neutral-400" />
                      <input
                        type={showPw ? "text" : "password"}
                        value={confirmPw}
                        onChange={(e) => setConfirmPw(e.target.value)}
                        placeholder="••••••••"
                        className="w-full bg-transparent outline-none placeholder:text-neutral-500 text-[15px]"
                        autoComplete="new-password"
                        required
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="mt-2 w-full rounded-lg border border-sky-600/40 bg-sky-500/10 px-4 py-2.5 text-[15px] font-medium text-sky-200 hover:bg-sky-500/20 disabled:opacity-50"
                  >
                    {loading ? "Creating account…" : "Sign up"}
                  </button>

                  <Turnstile
                    siteKey="0x4AAAAAABHcWCVoBFGccBYv"
                    onSuccess={(token) => setTurnstileToken(token)}
                    options={{ theme: "dark" }}
                    ref={turnstileRef}
                  />
                </form>
              )}
            </div>

            <div className="rounded-b-2xl border-t border-neutral-800 bg-[#0a1224]/60 px-8 py-4 text-[13px] text-neutral-400">
              Already have an account?{" "}
              <a href="/login" className="text-sky-300 hover:text-sky-200">
                Sign in
              </a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
